import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Play, Check, ArrowRight, Users, Scroll, 
  Target, HandFist, Lightning, CaretRight, CaretLeft, Cross
} from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';

const StartHerePage = () => {
  const { user, updateProfile, isPaidMember } = useAuth();
  const navigate = useNavigate();
  const [completedSteps, setCompletedSteps] = useState({
    welcome: false,
    rules: false,
    oath: false,
    howToWin: false,
    introduce: false
  });

  const handleCompleteStep = (step) => {
    setCompletedSteps(prev => ({ ...prev, [step]: true }));
  };

  const handleCompleteOnboarding = async () => {
    await updateProfile({ onboarding_completed: true });
    toast.success('Onboarding complete! Welcome to the brotherhood.');
    navigate('/community');
  };

  const allStepsComplete = Object.values(completedSteps).every(Boolean);

  const steps = [
    {
      id: 'welcome',
      title: 'Welcome to POWR',
      icon: Play,
      content: (
        <div className="space-y-6">
          <div className="aspect-video bg-[#121212] border border-white/10 flex items-center justify-center">
            <div className="text-center">
              <Play size={48} className="text-[#C4A661] mx-auto mb-4" weight="fill" />
              <p className="text-white/40">Welcome Video Coming Soon</p>
            </div>
          </div>
          <div className="space-y-4 text-white/70 leading-relaxed">
            <p>
              Brother, welcome to POWR — <span className="font-editorial italic text-[#F4F0E6]">Purpose. Ownership. Wealth. Responsibility.</span>
            </p>
            <p>
              This is not just another community. This is a brotherhood of Black men who are serious about growth, 
              faith, family, and building real wealth. You're here because you're ready for more.
            </p>
            <p>
              Inside POWR, you'll find men who will challenge you, support you, and hold you accountable. 
              This is an environment designed to push you toward your potential.
            </p>
            <p className="text-[#F4F0E6] font-semibold text-lg mt-6">
              The only question is: Are you ready to do the work?
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'rules',
      title: 'Community Rules',
      icon: Scroll,
      content: (
        <div className="space-y-6">
          <p className="text-white/70">
            To maintain the integrity of this brotherhood, we have a few non-negotiable rules:
          </p>
          <div className="space-y-4">
            {[
              { rule: 'Show Respect', desc: 'Every brother here is on a journey. Respect where they are, even while pushing them forward.' },
              { rule: 'Add Value', desc: 'Don\'t just consume. Share your wins, your lessons, your struggles. The community grows when you contribute.' },
              { rule: 'No Excuses', desc: 'This is a space for action, not complaints. Own your situation and work to change it.' },
              { rule: 'Keep It Real', desc: 'Authenticity over performance. Be honest about where you are. We grow from truth, not image.' },
              { rule: 'Protect the Brotherhood', desc: 'What\'s shared here stays here. We build trust by keeping confidence.' },
              { rule: 'Execute', desc: 'Information without action is worthless. Apply what you learn. Do the work.' }
            ].map((item, i) => (
              <div key={i} className="bg-[#121212] border border-white/10 p-5">
                <h4 className="text-[#C4A661] font-bold mb-2">{item.rule}</h4>
                <p className="text-white/50 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      )
    },
    {
      id: 'oath',
      title: 'The POWR Oath',
      icon: HandFist,
      content: (
        <div className="space-y-6">
          <p className="text-white/70">
            Before you enter fully, we ask every brother to take the POWR Oath. This is your commitment to yourself and the brotherhood.
          </p>
          <div className="bg-gradient-to-br from-[#121212] to-[#050505] border border-[#C4A661]/30 p-8">
            <h4 className="text-[#C4A661] font-bold text-xl mb-6 text-center">The POWR Oath</h4>
            <div className="space-y-4 text-white/70 leading-relaxed font-editorial italic">
              <p>I commit to walking in PURPOSE — knowing why I exist and aligning my actions with my calling.</p>
              <p>I commit to OWNERSHIP — taking full responsibility for my life, my family, and my results.</p>
              <p>I commit to building WEALTH — not just money, but health, relationships, knowledge, and legacy.</p>
              <p>I commit to RESPONSIBILITY — leading my family, serving my community, and becoming the man I was created to be.</p>
              <p className="pt-4 text-[#F4F0E6] font-semibold not-italic">
                I am a man of faith. I am a builder. I am part of the POWR brotherhood.
              </p>
            </div>
          </div>
          <p className="text-white/40 text-sm text-center">
            By completing this step, you affirm your commitment to the POWR Oath.
          </p>
        </div>
      )
    },
    {
      id: 'howToWin',
      title: 'How to Win Inside POWR',
      icon: Target,
      content: (
        <div className="space-y-6">
          <p className="text-white/70">
            Here's how to get the most out of your POWR membership:
          </p>
          <div className="space-y-4">
            {[
              { num: '01', title: 'Show Up Daily', desc: 'Check in with the community every day. Engagement creates momentum.' },
              { num: '02', title: 'Post in Accountability', desc: 'Share what you\'re working on. Let the brothers hold you to your word.' },
              { num: '03', title: 'Celebrate Wins', desc: 'No win is too small. Share your progress. It inspires others and reinforces your own discipline.' },
              { num: '04', title: 'Ask for Help', desc: 'Stuck? Ask. This brotherhood exists to support you. No one here succeeds alone.' },
              { num: '05', title: 'Support Other Brothers', desc: 'Comment on posts. Offer encouragement. Share what you know. We rise together.' },
              { num: '06', title: 'Take the Training', desc: 'The courses and content are designed to transform you. Don\'t just read — apply.' }
            ].map((item, i) => (
              <div key={i} className="flex gap-5 p-5 bg-[#121212] border border-white/10">
                <span className="text-[#C4A661] font-bold text-xl">{item.num}</span>
                <div>
                  <h4 className="font-bold mb-1 text-[#F4F0E6]">{item.title}</h4>
                  <p className="text-white/50 text-sm">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )
    },
    {
      id: 'introduce',
      title: 'Introduce Yourself',
      icon: Users,
      content: (
        <div className="space-y-6">
          <p className="text-white/70">
            The final step: Let the brotherhood know who you are.
          </p>
          <div className="bg-[#121212] border border-white/10 p-6">
            <h4 className="font-bold mb-4 text-[#F4F0E6]">Post Template</h4>
            <div className="bg-[#050505] p-5 border border-white/10 text-white/60 text-sm space-y-2 font-mono">
              <p className="text-[#F4F0E6]">What's up, POWR brothers!</p>
              <p className="text-white/40">My name is [YOUR NAME]</p>
              <p className="text-white/40">I'm from [LOCATION]</p>
              <p className="text-white/40">I'm here because [WHY YOU JOINED]</p>
              <p className="text-white/40">What I'm building: [YOUR GOALS]</p>
              <p className="text-white/40">One thing I need accountability on: [YOUR FOCUS]</p>
              <p className="text-[#F4F0E6]">Ready to grow with you all. Let's build.</p>
            </div>
          </div>
          <p className="text-white/40 text-sm">
            Go to the Community Feed and post your introduction. This is how you connect with the brotherhood.
          </p>
          <Link to="/community">
            <Button className="w-full btn-gold h-12" data-testid="go-to-community-btn">
              Go to Community to Post
              <CaretRight size={18} className="ml-2" />
            </Button>
          </Link>
        </div>
      )
    }
  ];

  const [activeStep, setActiveStep] = useState(0);

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="nav-glass border-b border-white/10">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 py-4 flex items-center justify-between">
          <Link to="/community" className="text-2xl font-bold tracking-tight text-[#C4A661]">POWR</Link>
          <Link to="/community" className="flex items-center gap-2 text-white/60 hover:text-[#F4F0E6] transition-colors text-sm uppercase tracking-widest">
            <CaretLeft size={18} weight="bold" />
            Back to Dashboard
          </Link>
        </div>
      </header>
      
      {/* Scripture Banner */}
      <div className="bg-[#C4A661] py-4">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 text-center">
          <p className="text-[#0A0A0A] text-sm font-medium">
            <Cross size={14} className="inline-block mr-2 -mt-0.5" weight="bold" />
            "Trust in the Lord with all your heart and lean not on your own understanding." — Proverbs 3:5
          </p>
        </div>
      </div>
      
      {/* Hero Content - Light Section */}
      <div className="bg-[#FDFDFD] py-12">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#C4A661] mb-2">Onboarding</p>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tighter text-[#0A0A0A] mb-3">
              Start Your <span className="text-[#C4A661]">Journey</span>
            </h1>
            <p className="text-[#525252] text-lg max-w-xl mx-auto">
              Complete your onboarding to get the most out of POWR.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto max-w-4xl px-6 md:px-12 lg:px-24 py-12">
        {/* Progress Bar */}
        <div className="mb-10">
          <div className="flex items-center gap-2">
            {steps.map((step, i) => (
              <div key={step.id} className="flex-1 flex items-center">
                <div className={`h-1 w-full ${
                  completedSteps[step.id] 
                    ? 'bg-[#C4A661]' 
                    : activeStep >= i 
                      ? 'bg-[#C4A661]/30' 
                      : 'bg-white/10'
                }`} />
              </div>
            ))}
          </div>
          <p className="text-sm text-white/40 mt-3">
            {Object.values(completedSteps).filter(Boolean).length} of {steps.length} steps complete
          </p>
        </div>

        {/* Steps Navigation */}
        <div className="grid md:grid-cols-[280px_1fr] gap-10">
          {/* Step List */}
          <div className="space-y-2">
            {steps.map((step, i) => (
              <button
                key={step.id}
                onClick={() => setActiveStep(i)}
                className={`w-full flex items-center gap-3 px-5 py-4 text-left transition-all ${
                  activeStep === i 
                    ? 'bg-[#121212] border-l-2 border-[#C4A661] text-[#F4F0E6]' 
                    : 'text-white/50 hover:text-[#F4F0E6] hover:bg-white/5'
                }`}
              >
                {completedSteps[step.id] ? (
                  <Check size={20} className="text-[#C4A661]" weight="bold" />
                ) : (
                  <step.icon size={20} />
                )}
                <span className="text-sm">{step.title}</span>
              </button>
            ))}
          </div>

          {/* Step Content */}
          <motion.div
            key={activeStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="card-premium p-8">
              <div className="flex items-center gap-4 mb-8">
                {React.createElement(steps[activeStep].icon, { 
                  size: 32, 
                  className: 'text-[#C4A661]',
                  weight: 'duotone'
                })}
                <h2 className="text-2xl font-bold text-[#F4F0E6]">{steps[activeStep].title}</h2>
              </div>

              {steps[activeStep].content}

              {/* Step Actions */}
              <div className="mt-10 pt-8 border-t border-white/10 flex items-center justify-between">
                {activeStep > 0 ? (
                  <Button 
                    variant="outline" 
                    onClick={() => setActiveStep(activeStep - 1)}
                    className="btn-outline"
                  >
                    Previous
                  </Button>
                ) : <div />}

                {completedSteps[steps[activeStep].id] ? (
                  activeStep < steps.length - 1 ? (
                    <Button 
                      onClick={() => setActiveStep(activeStep + 1)}
                      className="btn-gold"
                    >
                      Next Step
                      <ArrowRight size={18} className="ml-2" />
                    </Button>
                  ) : (
                    <Button 
                      onClick={handleCompleteOnboarding}
                      className="btn-gold"
                      data-testid="complete-onboarding-btn"
                    >
                      Complete Onboarding
                      <Check size={18} className="ml-2" weight="bold" />
                    </Button>
                  )
                ) : (
                  <Button 
                    onClick={() => handleCompleteStep(steps[activeStep].id)}
                    className="bg-[#121212] border border-white/20 text-[#F4F0E6] hover:bg-white/10 px-8 py-4"
                    data-testid="mark-complete-btn"
                  >
                    Mark as Complete
                    <Check size={18} className="ml-2" />
                  </Button>
                )}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Complete All Button */}
        {allStepsComplete && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-12 text-center"
          >
            <div className="bg-gradient-to-r from-[#C4A661]/10 via-[#121212] to-[#C4A661]/10 border border-[#C4A661]/30 p-10">
              <h3 className="text-2xl font-bold mb-4 text-[#F4F0E6]">You're Ready, Brother!</h3>
              <p className="text-white/50 mb-6">
                You've completed all onboarding steps. Time to engage with the community.
              </p>
              <Button 
                onClick={handleCompleteOnboarding}
                className="btn-gold h-12 px-10"
                data-testid="enter-brotherhood-btn"
              >
                Enter the Brotherhood
                <Lightning size={20} className="ml-2" weight="fill" />
              </Button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default StartHerePage;
